import * as React from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {axiosInstances} from "../lib/axios";
import {ClickStat} from "../lib/types/types";
import {User} from "../App";
import {useInterval} from "primereact/hooks";
import moment from "moment";
import "moment-timezone"
import {ConfirmDialog, confirmDialog} from 'primereact/confirmdialog';
import {useToast} from "../lib/hooks/useToast";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {FloatLabel} from "primereact/floatlabel";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";

interface Props {
    clickStats: ClickStat,
    user: User;
    blockUi: (shouldBlock: boolean) => void
}

interface ButtonInterface {
    name: string;
    id: number;
    description: string
}

const MainScreen: React.FC<Props> = props => {
    const didMountRef = useRef(false);
    const [buttonsToRender, setButtonsToRender] = useState<ButtonInterface[]>([]);
    const [clickStats, setClickStats] = useState(props.clickStats);
    const [shouldReset, setShouldReset] = useState(false);
    const [showOptionsDialog, setShowOptionsDialog] = useState(false);
    const [showConfirmResetDailyDialog, setShowConfirmResetDailyDialog] = useState(false);
    const [selectedButton, setSelectedButton] = useState<1 | 2 | 3 | 4>();
    const [clickValue, setClickValue] = useState<number>()
    const {showToast} = useToast();


    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (!shouldReset) return;

        props.blockUi(true);
        axiosInstances.mainAxios.get<{ clickStatDTO: ClickStat }>("v1/stat")
            .then(resp => {
                setClickStats(resp.data.clickStatDTO)
                props.blockUi(false);
            })

    }, [shouldReset]);

    useEffect(() => {
        props.blockUi(true);
        axiosInstances.mainAxios.get<{ buttons: ButtonInterface[] }>("v1/click")
            .then(resp => {
                setButtonsToRender(resp.data.buttons)
                props.blockUi(false);
            })
    }, [])

    //Run every 30 seconds to check if the time is past 00:00 Central American Time. If so - fetch the stats again
    useInterval(
        () => {
            if (shouldReset) {
                setShouldReset(false);
                return;
            }
            // Get the current time in Central Time
            const centralTime = moment().tz("America/Chicago");

            // Check if it's past midnight (00:00)
            if (centralTime.hour() === 0 && centralTime.minute() > 0 || centralTime.hour() > 0) {
                setShouldReset(true);
            }
        },
        30000,   //delay (ms)
        true  //condition (when)
    );

    const handleButtonClick = (buttonId: number) => {
        props.blockUi(true);
        axiosInstances.mainAxios.post<{
            clickStat: ClickStat
        }>("v1/click", {timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone, buttonId: buttonId})
            .then(resp => {
                setClickStats(resp.data.clickStat)
                props.blockUi(false);
            })
    }

    const showConfirmUnclick = (buttonId: number) => {
        confirmDialog({
            message: 'Are you sure you want to proceed?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: () => handleButtonUnclick(buttonId),
            reject: () => 0
        });
    }

    const handleButtonUnclick = (buttonId: number) => {
        props.blockUi(true);
        axiosInstances.mainAxios.post("v1/unclick", {buttonId})
            .then(resp => {
                if (resp.data) {
                    showToast("success", "Successfull operaiton", "");
                    setClickStats(resp.data.clickStat)
                    props.blockUi(false);

                }
            }).catch(err => {
            showToast("error", "An error occured", err.response?.data?.errorMessage);
        })

    }

    const buttonsToRenderMemo = useMemo(() => {
        return buttonsToRender.map((button, index) => {
            let clickCount = 0;
            switch (index) {
                case 0:
                    clickCount = clickStats.clickCount1
                    break;
                case 1:
                    clickCount = clickStats.clickCount2
                    break;
                case 2:
                    clickCount = clickStats.clickCount3
                    break;
                case 3:
                    clickCount = clickStats.clickCount4
                    break;
            }
            return <div key={button.id}
                        style={{
                            boxShadow: "inset -4px -4px 8px rgba(255, 255, 255, 0.3),inset 4px 4px 8px rgba(0, 0, 0, 0.2)",
                            backgroundColor: "#4CAF50",
                            color: "white"
                        }}
                        onClick={() => handleButtonClick(button.id)}
                        className="text-2xl cursor-pointer border-round-3xl m-3 p-2 h-15rem font-bold flex align-items-center justify-content-center col-10 md:col-4">
                <span className={"flex justify-content-end w-6"}>{button.name}</span>
                <div className={"flex justify-content-end w-6 mr-4"}>
                    <span className={"px-4 py-2 border-2 border-round-2xl ml-4"}
                          style={{borderColor: "black", backgroundColor: "#1E487D"}}>{clickCount}</span>

                    <span className={"px-3 py-2 border-2 border-round-2xl ml-2"} onClick={(e) => {
                        e.stopPropagation()
                        showConfirmUnclick(button.id)
                    }}
                          style={{borderColor: "black", backgroundColor: "#f83c50"}}>
                        <i className="pi pi-minus"></i>
                    </span>

                    {/*<Button className={"ml-3"} icon={"pi pi-minus"}/>*/}
                </div>
            </div>
        })
    }, [buttonsToRender, clickStats])


    const setClickCount = () => {
        if (!selectedButton || !clickValue || clickValue <= 0) return;
        props.blockUi(true);

        axiosInstances.mainAxios.post("v1/clickmulti", {buttonId: selectedButton, count: clickValue})
            .then(resp => {
                if (resp.data) {
                    props.blockUi(false);

                    showToast("success", "Successfull operaiton", "");
                    setClickStats(resp.data.clickStat);
                    setShowOptionsDialog(false);
                    setSelectedButton(undefined);
                    setClickValue(undefined);
                }
            }).catch(err => {
            showToast("error", "An error occured", err.response?.data?.errorMessage);
        })

    }

    const handleResetStatistic = () => {
        axiosInstances.mainAxios.post("v1/reset", {buttonId: selectedButton, count: clickValue})
            .then(resp => {
                if (resp.data) {
                    props.blockUi(false);

                    showToast("success", "Successfull operaiton", "");
                    setClickStats(resp.data.clickStat);
                    setShowConfirmResetDailyDialog(false);
                }
            }).catch(err => {
            showToast("error", "An error occured", err.response?.data?.errorMessage);
        })
    }

    // @ts-ignore
    return <>
        <div className="flex h-full align-items-center flex-column">
            <div className={"flex justify-content-center align-items-start w-full p-6"}>
                <h2 style={{color: "white"}}>{props.user.locationTag}</h2>
            </div>
            <div className="w-full flex align-items-center justify-content-center grid">
                {buttonsToRenderMemo}
            </div>

            {props.user.enableExtraOptions &&
                <>
                    <div className={"flex justify-content-center mt-auto w-6"}>
                        <Button style={{backgroundColor: "#4CAF50", fontSize: "1.25rem"}} className={"w-full"}
                                label={"Multi click"} onClick={() => {
                            setShowOptionsDialog(true)
                        }}/>
                    </div>

                    <div className={"flex justify-content-center w-6 mt-3"}>
                        <Button style={{backgroundColor: "#fb1034", fontSize: "1.25rem"}} className={"w-full"}
                                label={"Reset daily"} onClick={() => {
                            setShowConfirmResetDailyDialog(true)
                        }}/>
                    </div>
                </>
            }


        </div>
        <ConfirmDialog/>

        <Dialog header={"Are you sure you want to reset the statistic daily?"} onHide={() => setShowConfirmResetDailyDialog(false)} visible={showConfirmResetDailyDialog}>

            <div className={"flex w-full justify-content-between"}>
                <Button className={"w-3"} label={"Yes"} severity={"success"} onClick={handleResetStatistic} />
                <Button className={"w-3"} label={"No"} severity={"danger"} onClick={() => setShowConfirmResetDailyDialog(false)} />

            </div>

        </Dialog>

        <Dialog header="Multi click" visible={showOptionsDialog} style={{width: '50vw'}} onHide={() => {
            setShowOptionsDialog(false)
        }}>
            <div className={"flex grid mt-4"}>
                <div className={"col-12 md:col-6"}>
                    <FloatLabel>
                        <Dropdown id="buttonId" className={"p-inputtext-lg w-full"} value={selectedButton}
                                  onChange={(e) => setSelectedButton(e.value)} options={buttonsToRender}
                                  optionLabel={"description"} optionValue={"id"}/>
                        <label htmlFor="email">Meal</label>
                    </FloatLabel>
                </div>

                <div className={"col-12 md:col-6"}>
                    <FloatLabel>
                        <InputNumber id="clickCount" className={"p-inputtext-lg w-full"} value={clickValue}
                                     pt={{
                                         input: {
                                             root: {
                                                 className: "w-full"
                                             }
                                         }
                                     }}
                                     onValueChange={(e) => setClickValue(e.value as number)}/>
                        <label htmlFor="email">Add count</label>
                    </FloatLabel>
                </div>
            </div>

            <div className={"flex grid mt-3"}>
                <div className={"col-12 md:col-6"}>
                    <Button label={"Save"} className={"w-full"} onClick={setClickCount}/>
                </div>

                <div className={"col-12 md:col-6"}>
                    <Button label={"Cancel"} className={"w-full"}/>
                </div>
            </div>
        </Dialog>
    </>
};

export default MainScreen
